/**
 * @module users
 * @description User management, roles, and profile operations
 */

import type { ModuleManifest, ModuleContext } from '@gzl10/nexus-sdk'
import { createUsersRoutes } from './users.routes.js'
import { createUsersService } from './users.service.js'
import { userEntity, roleEntity, userRoleEntity } from './users.entity.js'

// Re-exports
export { createUsersService } from './users.service.js'
export { userEntity, roleEntity, userRoleEntity } from './users.entity.js'

// Types from users.types.ts
export type {
  User,
  Role,
  UserWithoutPassword,
  UserPresence,
  UserWithRoles,
  RoleWithCounts,
  UsersService,
  RolesService
} from './users.types.js'

export const usersModule: ModuleManifest = {
  name: 'users',
  label: { en: 'Users & Roles', es: 'Usuarios y roles' },
  icon: 'mdi:account-group-outline',
  description: { en: 'User accounts, authentication profiles, roles and permissions', es: 'Cuentas de usuario, perfiles de autenticación, roles y permisos' },
  type: 'core',
  category: 'identity',
  dependencies: ['logger'],
  // Import dinámico para evitar posibles ciclos
  seed: async (ctx) => {
    const { seed } = await import('./users.seed.js')
    await seed(ctx)
  },

  init: async (ctx: ModuleContext) => {
    const svc = await createUsersService(ctx)
    ctx.services.register('users', svc)
    // Register roles service separately so entity routes for /roles use the custom service (with users_count).
    ctx.services.register('roles', svc.roles as never)
    ctx.core.logger.debug('Users service registered')
  },

  routes: createUsersRoutes,
  routePrefix: '/users',
  // Orden: roles primero, luego users, finalmente user_roles (FK a ambos)
  definitions: [roleEntity, userEntity, userRoleEntity]
}
